import { UpdateFilterType } from "components/ReusableHeaderFilter/ColumnHeaderMultiSelectFilter";
import {
  FILTER_BOOKS_PENDING,
  FILTER_ETD_DUE_TODAY,
  FILTER_ETD_OVERDUE,
  FILTER_ETD_UNSET,
  FILTER_GLOBAL_STATUS,
  FILTER_GLOBAL_STATUS_COMPLETED,
  FILTER_IN_PROGRESS,
  FILTER_INFORMATION_REQUIRED,
  FILTER_MANDATORY_INFORMATION_MISSING,
  FILTER_INTERNAL_REVIEW,
  FILTER_READY_TO_PICK,
  STATUS_DRAFT_SHARED,
  STATUS_ON_HOLD,
  STATUS_READY_TO_FILE,
  STATUS_UNPAID,
} from "components/TaskTracker/Task1120FilterOptions";
import { Task1120Table } from "components/TaskTracker/Task1120Table";
import { TrackerFilterOptions } from "components/TaskTracker/Task1120TrackerFilter";
import { TaskTrackerHeader } from "components/TaskTracker/TaskTrackerHeader";
import DashboardContainer from "components/dashboard/DashboardContainer";
import TaskTabBar from "components/tasks/taskTabBar";
import { BOTH } from "constants/calendarTypes";
import dayjs from "dayjs";
import { useFilters } from "hooks/useFilter";
import { useQuery } from "hooks/useQuery";
import { useCallback, useMemo, useState } from "react";
import { useGetTask1120TrackerQuery } from "store/apis/Task1120Tracker";
import { useRoleBasedView } from "hooks/useRoleBasedView";
import { useGetUserProfileQuery } from "store/apis/profile";
import { useGetAssignAgentsQuery } from "store/apis/agents";
import { Task1120BulkActionsFloat } from "./Task1120BulkActionsFloat";

const EMPTY_AGENTS: Array<{ uuid: string; profile_id: string }> = [];

const initialValue: TrackerFilterOptions = {
  SERVICE_TEAM: null,
  SERVICE_TEAM_MEMBER: null,
  INFO_NEEDED_CLIENT: false,
  BOOKS_PENDING: false,
  READY_TO_BE_PICKED_UP: false,
  PREP_IN_PROGRESS: false,
  MANDATORY_INFO_MISSING: false,
  INTERNAL_REVIEW: false,
  DRAFT_SHARED: false,
  READY_TO_FILE: false,
  FILED: false,
  AGENT: null,
  START_DATE: "",
  END_DATE: "",
  SELECT_PERIOD: "",
  ETD: "",
  BOOKKEEPING_STATUS: null,
  TASK_7004_STATUS: null,
  TASK_1120_STATUS: null,
  TASK_5471_STATUS: null,
  TASK_5472_STATUS: null,
  TASK_R_AND_D_STATUS: null,
  FBAR_STATUS: null,
  STATE_REGISTRATIONS: null,
  PREV_YEAR_FORM_1120_STATUS: null,
  PREV_YEAR_FORM_5471_STATUS: null,
  PREV_YEAR_FORM_5472_STATUS: null,
  BOOKKEEPERS: null,
  UNPAID: false,
  FORMATION_TYPE: "",
  ENTITY_TAXED_AS: null,
  PROFITABLE_COMPANY_TAG: null,
  ETD_START_DATE: "",
  ETD_END_DATE: "",
  ETD_SELECT_PERIOD: "",
  UNSET: false,
  OVERDUE: false,
  DUE_TODAY: false,
  ON_HOLD: false,
  REVIEWER: null,
  PREPARER_ASSIGNED_START_DATE: "",
  PREPARER_ASSIGNED_END_DATE: "",
  PREPARER_ASSIGNED_SELECT_PERIOD: "",
  REVIEWER_ASSIGNED_START_DATE: "",
  REVIEWER_ASSIGNED_END_DATE: "",
  REVIEWER_ASSIGNED_SELECT_PERIOD: "",
  FORM_1120_COMPLETED_START_DATE: "",
  FORM_1120_COMPLETED_END_DATE: "",
  FORM_1120_COMPLETED_SELECT_PERIOD: "",
  GLOBAL_STATUS: "",
  IS_URGENT: null,
  IS_AMENDMENT: null,
};

export const Task1120Tracker = () => {
  const { isCpa, isAdmin, isForeignCA } = useRoleBasedView();
  const isCpaOnly = isCpa && !isAdmin && !isForeignCA;
  const { data: profile } = useGetUserProfileQuery();
  const profileUuid = profile?.uuid;
  const llcFormationTypeUuid = process.env.PUBLIC_ENTITY_TYPE_LLC;

  const query = useQuery();
  const page = Number(query.get("page") || "1");
  const pageSize = Number(query.get("page_size")) || null;
  const searchTerm = query.get("search_term") || null;
  const selectedSeason = Number(
    query.get("season") || dayjs().subtract(1, "year").year()
  );
  const selectedTab = query.get("tab") || BOTH;
  const selectedCalendarYear = query.get("calendar_year") || BOTH;

  const { data: assignAgents = EMPTY_AGENTS } = useGetAssignAgentsQuery(
    {},
    { skip: !isAdmin }
  );

  const myAgentUuid =
    assignAgents.find((agent) => agent.profile_id === profileUuid)?.uuid ||
    null;

  const filterInitialValue = useMemo(
    () => ({
      ...initialValue,
      AGENT: isCpaOnly ? "" : myAgentUuid,
      SERVICE_TEAM_MEMBER: isCpaOnly && profileUuid ? profileUuid : "",
    }),
    [myAgentUuid, isCpaOnly, profileUuid]
  );

  const { values, updateFilter } = useFilters<TrackerFilterOptions>({
    initialValue: filterInitialValue,
    useQueryParams: true,
  });

  const isLLCSelected =
    !!llcFormationTypeUuid && values.FORMATION_TYPE === llcFormationTypeUuid;

  const [rowSelection, setRowSelection] = useState({});
  const [selectedEntityIds, setSelectedEntityIds] = useState<string[]>([]);

  const updateFilterTyped = updateFilter as UpdateFilterType &
    (<S extends keyof TrackerFilterOptions>(
      name: S,
      newValue: TrackerFilterOptions[S]
    ) => void);

  const informationRequire = query.get(FILTER_INFORMATION_REQUIRED) || null;
  const booksPending = query.get(FILTER_BOOKS_PENDING) || null;
  const readyToPick = query.get(FILTER_READY_TO_PICK) || null;
  const inProgress = query.get(FILTER_IN_PROGRESS) || null;
  const mandatoryInformationMissing =
    query.get(FILTER_MANDATORY_INFORMATION_MISSING) || null;
  const internalReview = query.get(FILTER_INTERNAL_REVIEW) || null;
  const unpaid = query.get(STATUS_UNPAID) || null;
  const globalStatusCompleted =
    query.get(FILTER_GLOBAL_STATUS_COMPLETED) || null;
  const readyToFile = query.get(STATUS_READY_TO_FILE) || null;
  const draftShared = query.get(STATUS_DRAFT_SHARED) || null;
  const onHold = query.get(STATUS_ON_HOLD) || null;

  const globalStatus = [];
  if (informationRequire) globalStatus.push(FILTER_INFORMATION_REQUIRED);
  if (booksPending) globalStatus.push(FILTER_BOOKS_PENDING);
  if (readyToPick) globalStatus.push(FILTER_READY_TO_PICK);
  if (inProgress) globalStatus.push(FILTER_IN_PROGRESS);
  if (internalReview) globalStatus.push(FILTER_INTERNAL_REVIEW);
  if (mandatoryInformationMissing)
    globalStatus.push(FILTER_MANDATORY_INFORMATION_MISSING);
  if (unpaid) globalStatus.push(STATUS_UNPAID);
  if (globalStatusCompleted) globalStatus.push(FILTER_GLOBAL_STATUS_COMPLETED);
  if (readyToFile) globalStatus.push(STATUS_READY_TO_FILE);
  if (draftShared) globalStatus.push(STATUS_DRAFT_SHARED);
  if (onHold) globalStatus.push(STATUS_ON_HOLD);

  const etdNoDue = query.get(FILTER_ETD_UNSET) || null;
  const etdOverdue = query.get(FILTER_ETD_OVERDUE) || null;
  const etdDueToday = query.get(FILTER_ETD_DUE_TODAY) || null;

  const etdTypeStatus = [];
  if (etdNoDue) etdTypeStatus.push("UN_SET");
  if (etdOverdue) etdTypeStatus.push("OVER_DUE");
  if (etdDueToday) etdTypeStatus.push("DUE_TODAY");

  const filters = {
    page_num: page,
    page_size: pageSize,
    season: selectedSeason,
    search_term: searchTerm,
    assigned_agents_uuids: values.AGENT || null,
    service_team_uuids: values.SERVICE_TEAM || null,
    tax_preparer_uuids: values.SERVICE_TEAM_MEMBER || null,
    start_pickup_date: values.START_DATE || null,
    end_pickup_date: values.END_DATE || null,
    etd_start_date: values.ETD_START_DATE || null,
    etd_end_date: values.ETD_END_DATE || null,
    task_1120_tracker_global_status:
      values.GLOBAL_STATUS || globalStatus.join(",") || null,
    entity_uuids: null,
    form_1120_bookkeeping_status: values.BOOKKEEPING_STATUS || null,
    bookkeeper_uuids: values.BOOKKEEPERS || null,
    form_7004_status: values.TASK_7004_STATUS || null,
    form_1120_status: values.TASK_1120_STATUS || null,
    form_5471_status: values.TASK_5471_STATUS || null,
    form_5472_status: values.TASK_5472_STATUS || null,
    form_r_and_d_status: values.TASK_R_AND_D_STATUS || null,
    state_registrations_uuids: values.STATE_REGISTRATIONS || null,
    previous_season_form_1120_status: values.PREV_YEAR_FORM_1120_STATUS || null,
    previous_season_form_5471_status: values.PREV_YEAR_FORM_5471_STATUS || null,
    previous_season_form_5472_status: values.PREV_YEAR_FORM_5472_STATUS || null,
    tax_year_end: selectedCalendarYear,
    payment_tab_filter: selectedTab,
    formation_type: values.FORMATION_TYPE || null,
    entity_taxed_as: isLLCSelected ? values.ENTITY_TAXED_AS || null : null,
    tag_filter: values.PROFITABLE_COMPANY_TAG
      ? process.env.PUBLIC_COMPANY_GROUP_PROFITABLE_TAG_ID
      : null,
    fbar_status: values.FBAR_STATUS || null,
    etd_type: etdTypeStatus.join(",") || null,
    reviewer_uuids: values.REVIEWER || null,
    preparer_assigned_start_date: values.PREPARER_ASSIGNED_START_DATE || null,
    preparer_assigned_end_date: values.PREPARER_ASSIGNED_END_DATE || null,
    reviewer_assigned_start_date: values.REVIEWER_ASSIGNED_START_DATE || null,
    reviewer_assigned_end_date: values.REVIEWER_ASSIGNED_END_DATE || null,
    form_1120_completed_start_date:
      values.FORM_1120_COMPLETED_START_DATE || null,
    form_1120_completed_end_date: values.FORM_1120_COMPLETED_END_DATE || null,
    is_urgent: values.IS_URGENT ?? null,
    is_amendment: values.IS_AMENDMENT || null,
  };

  const { data, isLoading, isSuccess, isFetching } =
    useGetTask1120TrackerQuery(filters);
  const { per_page, total_count, total_pages } = data || {};
  const task = useMemo(() => data?.["1120 Tasks"] || [], [data]);
  const isEmpty = isSuccess && !task.length;

  const resetFilter = () => {
    Object.keys(initialValue).forEach((key) => {
      updateFilter(
        key as keyof TrackerFilterOptions,
        initialValue[key as keyof TrackerFilterOptions]
      );
    });
  };

  const resetSelection = useCallback(() => {
    setRowSelection({});
    setSelectedEntityIds([]);
  }, []);

  const selectedRowsServiceTeam = useMemo(() => {
    return (
      task
        .filter(
          ({ entity, assigned_team }) =>
            selectedEntityIds.includes(entity.uuid) && assigned_team?.uuid
        )
        .map(({ assigned_team }) => assigned_team?.uuid!) || []
    );
  }, [task, selectedEntityIds]);

  const selectedTaskIds = useMemo(() => {
    return task
      .filter(({ entity }) => selectedEntityIds.includes(entity.uuid))
      .map((task) => task?.task_uuid || "");
  }, [task, selectedEntityIds]);

  return (
    <DashboardContainer className="t-h-full">
      <DashboardContainer.Header>
        <TaskTabBar defaultValue="admin/task-1120-tracker" />
        <div className="t-px-5">
          <TaskTrackerHeader
            values={values}
            updateFilter={updateFilter}
            totalPage={total_pages}
            itemsPerPage={per_page}
            totalItemCount={total_count}
            resetFilter={resetFilter}
            filters={JSON.stringify(filters)}
            isUpdating={isFetching && !isLoading}
            isSuccess={isSuccess}
          />
        </div>
      </DashboardContainer.Header>
      <DashboardContainer.Content className="t-px-5 t-mt-3">
        <Task1120Table
          task={task}
          perPage={per_page || 20}
          values={values}
          updateFilter={updateFilterTyped}
          isLoading={isLoading}
          isSuccess={isSuccess}
          isEmpty={isEmpty}
          rowSelection={rowSelection}
          setRowSelection={setRowSelection}
          selectedEntityIds={selectedEntityIds}
          setSelectedEntityIds={setSelectedEntityIds}
        />
        <Task1120BulkActionsFloat
          selectedTaskIds={selectedTaskIds}
          resetSelection={resetSelection}
          selectedRowsServiceTeam={selectedRowsServiceTeam}
        />
      </DashboardContainer.Content>
    </DashboardContainer>
  );
};
